import { useState, useEffect, useCallback, Suspense } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DynamicIcon } from 'lucide-react/dynamic';
import type { IconName } from 'lucide-react/dynamic';
import { FileText, Info, Camera, Plus, Minus } from 'lucide-react';
import { Modal } from '@/components/Modal';
import { FileUpload } from '@/components/FileUpload';
import { useToast } from '@/contexts/ToastContext';
import { useSchoolTimezone } from '@/contexts/TimezoneContext';
import { getTodayInSchoolTZ } from '@/utils/dateFormatting';
import { behaviorApi, behaviorCategoriesApi, locationTypesApi, schoolConfigApi } from '@/services/api';
import { useSeverityEnabled } from '@/hooks/useSeverityEnabled';
import { usePositiveBehaviorsEnabled } from '@/hooks/usePositiveBehaviorsEnabled';
import { usePointSettings } from '@/hooks/usePointsEnabled';
import { useTerminology } from '@/hooks/useTerminology';
import { useDialog } from '@/contexts/DialogContext';
import { buttonStyles } from '@/styles/buttons';

interface BehaviorTrackingProps {
  isOpen: boolean;
  onClose: () => void;
  studentId: string;
  studentName: string;
  blockId?: string; // For classroom behaviors
  blockName?: string;
  mode: 'single' | 'bulk';
  studentIds?: string[]; // For bulk mode (Phase 6)
  /** Map of student IDs to display names, for bulk error reporting */
  studentNames?: Record<string, string>;
  /**
   * Available blocks for dropdown selection mode.
   * When provided, shows a dropdown instead of "In class / Outside class" toggle.
   * Used when opening from BehaviorHistoryModal.
   *
   * Component modes based on props:
   * 1. availableBlocks provided → Dropdown mode (select from list + "Outside class" option)
   * 2. blockId provided (no availableBlocks) → Toggle mode ("In class" vs "Outside class")
   * 3. Neither provided → Location-only mode (for support staff)
   */
  availableBlocks?: Array<{ id: string; name: string }>;
}

/**
 * BehaviorTracking Modal (Phase 5)
 *
 * Quick 3-second workflow for logging student behaviors.
 *
 * Features:
 * - Single student mode (fully working)
 * - Bulk mode structure (UI integration deferred to Phase 6)
 * - Category selection with colors/icons
 * - Severity toggle with school config permission check (✅ Implemented)
 * - Location behaviors support (classroom vs outside class)
 * - Notes validation based on category requirements
 */
export function BehaviorTracking({
  isOpen,
  onClose,
  studentId,
  studentName,
  blockId,
  blockName,
  mode = 'single',
  studentIds = [],
  studentNames,
  availableBlocks,
}: BehaviorTrackingProps) {
  const { success, error } = useToast();
  const { confirm: confirmDialog, alert: alertDialog } = useDialog();
  const queryClient = useQueryClient();
  const { timezone } = useSchoolTimezone();
  const severityEnabled = useSeverityEnabled();
  const positiveBehaviorsEnabled = usePositiveBehaviorsEnabled();
  const { pointsEnabled, allowPointOverride, pointOverrideMin, pointOverrideMax } = usePointSettings();
  const term = useTerminology();

  // Form state
  const [behaviorDate, setBehaviorDate] = useState(getTodayInSchoolTZ(timezone));
  const [behaviorLocation, setBehaviorLocation] = useState<'classroom' | 'location'>(
    blockId ? 'classroom' : 'location'
  );
  // Selected block ID for dropdown mode (when availableBlocks is provided)
  const [selectedBlockId, setSelectedBlockId] = useState<string>(blockId || '');

  // Validate blockId against availableBlocks - fall back to location mode if invalid
  // This prevents using a stale blockId from historical data when student is no longer enrolled
  useEffect(() => {
    if (availableBlocks && blockId) {
      if (!availableBlocks.some((b) => b.id === blockId)) {
        setSelectedBlockId('');
        setBehaviorLocation('location');
      }
    }
  }, [availableBlocks, blockId]);

  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedSeverity, setSelectedSeverity] = useState<'minor' | 'major'>('minor');
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [severityInfoExpanded, setSeverityInfoExpanded] = useState(false);
  // Points override state (only used when pointsEnabled && allowPointOverride)
  const [pointsOverride, setPointsOverride] = useState<number | null>(null);
  const [pointsInfoExpanded, setPointsInfoExpanded] = useState(false);

  // Photo upload state
  const [selectedPhotos, setSelectedPhotos] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadState, setUploadState] = useState<'idle' | 'creating' | 'uploading' | 'complete' | 'error'>('idle');

  const resetForm = useCallback(() => {
    setSelectedCategory('');
    setSelectedSeverity('minor');
    setSelectedLocation('');
    setNotes('');
    setBehaviorDate(getTodayInSchoolTZ(timezone));
    setPointsOverride(null);
    setPointsInfoExpanded(false);
    setSeverityInfoExpanded(false);

    // Reset photo state
    setSelectedPhotos([]);
    setUploadProgress(0);
    setUploadError(null);
    setUploadState('idle');
  }, [timezone]);

  // Reset form when modal opens or student changes (prevents stale selections)
  useEffect(() => {
    if (isOpen) {
      resetForm();
      setBehaviorLocation(blockId ? 'classroom' : 'location');
      setSelectedBlockId(blockId || '');
      setBehaviorDate(getTodayInSchoolTZ(timezone));
    }
  }, [isOpen, studentId, blockId, timezone, resetForm]);

  // Mobile detection for responsive text
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch behavior categories
  const { data: categories = [] } = useQuery({
    queryKey: ['behaviorCategories'],
    queryFn: async () => {
      const response = await behaviorCategoriesApi.getAll();
      return response.data; // ✅ React Query pattern: unwrap in queryFn
    },
    enabled: isOpen,
  });

  // Fetch location types
  const { data: locations = [] } = useQuery({
    queryKey: ['locationTypes'],
    queryFn: async () => {
      const response = await locationTypesApi.getAll();
      return response.data; // ✅ React Query pattern: unwrap in queryFn
    },
    enabled: isOpen && behaviorLocation === 'location',
  });

  // Fetch school config
  const { data: schoolConfig } = useQuery({
    queryKey: ['schoolConfig'],
    queryFn: () => schoolConfigApi.get(), // API already returns unwrapped data
    enabled: isOpen,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes (config rarely changes)
  });

  // Get selected category details
  const category = categories.find((c) => c.id === selectedCategory);

  // Get max notes length from school config
  const maxNotesLength = schoolConfig?.maxNotesLength ?? 500;

  // Check if severity override is allowed
  const canOverrideSeverity = schoolConfig?.allowSeverityOverride ?? true; // Default true if config not loaded
  const isSeverityLocked = !canOverrideSeverity && !!selectedCategory && !!category;

  // Update severity and points when category changes
  useEffect(() => {
    if (category) {
      setSelectedSeverity(category.defaultSeverity);
      // Reset points override to null (will use category default)
      setPointsOverride(null);
    }
  }, [category]);

  // Check if notes are required
  // When severity disabled, use requireNotesMajor as the unified "requireNotes" value
  const notesRequired = category
    ? severityEnabled
      ? (selectedSeverity === 'minor' && category.requireNotesMinor) ||
        (selectedSeverity === 'major' && category.requireNotesMajor)
      : category.requireNotesMajor
    : false;

  // Fallback icon component for DynamicIcon
  const FallbackIcon = () => <FileText size={24} aria-hidden="true" />;

  // Render icon using DynamicIcon with lazy loading
  const getIcon = (iconName: string | null, size: number = 24): React.ReactNode => {
    if (!iconName) return <FallbackIcon />;
    return (
      <Suspense fallback={<FallbackIcon />}>
        <DynamicIcon name={iconName as IconName} size={size} fallback={FallbackIcon} aria-hidden="true" />
      </Suspense>
    );
  };

  // Calculate effective points to display/use
  const effectivePoints = pointsOverride ?? category?.defaultPoints ?? 1;

  // Create behavior mutation
  const createMutation = useMutation({
    mutationFn: async (variables?: { acknowledgeBlockLimit?: boolean }) => {
      setUploadState('creating');
      setUploadError(null);

      let behaviorResponse;

      // Use selectedBlockId (from dropdown) if available, otherwise fall back to blockId prop
      const effectiveBlockId = selectedBlockId || blockId;

      if (mode === 'bulk') {
        // Bulk mode (Phase 6 - UI integration deferred)
        behaviorResponse = await behaviorApi.bulkCreate({
          studentIds,
          blockId: behaviorLocation === 'classroom' ? effectiveBlockId : undefined,
          locationId: behaviorLocation === 'location' ? selectedLocation : undefined,
          categoryId: selectedCategory,
          severity: selectedSeverity,
          behaviorDate,
          notes: notes || undefined,
          // Include points override if different from category default
          points: pointsOverride !== null ? pointsOverride : undefined,
          acknowledgeBlockLimit: variables?.acknowledgeBlockLimit,
        });
      } else {
        // Single mode (Phase 5 - fully working)
        behaviorResponse = await behaviorApi.create({
          studentId,
          blockId: behaviorLocation === 'classroom' ? effectiveBlockId : undefined,
          locationId: behaviorLocation === 'location' ? selectedLocation : undefined,
          categoryId: selectedCategory,
          severity: selectedSeverity,
          behaviorDate,
          notes: notes || undefined,
          // Include points override if different from category default
          points: pointsOverride !== null ? pointsOverride : undefined,
          acknowledgeBlockLimit: variables?.acknowledgeBlockLimit,
        });
      }

      // If photos are selected and we're in single mode, upload them
      if (
        mode === 'single' &&
        selectedPhotos.length > 0 &&
        'data' in behaviorResponse &&
        !Array.isArray(behaviorResponse.data)
      ) {
        const behaviorRecordId = behaviorResponse.data.id;
        setUploadState('uploading');

        const uploadResults: { success: boolean; file: File; error?: string }[] = [];

        for (let i = 0; i < selectedPhotos.length; i++) {
          const photo = selectedPhotos[i];

          try {
            await behaviorApi.uploadEvidence(behaviorRecordId, photo);
            uploadResults.push({ success: true, file: photo });
          } catch (err: unknown) {
            uploadResults.push({
              success: false,
              file: photo,
              error: err instanceof Error ? err.message : 'Upload failed',
            });
          }

          // Update progress
          setUploadProgress(((i + 1) / selectedPhotos.length) * 100);
        }

        // Check if any uploads failed
        const failedUploads = uploadResults.filter((r) => !r.success);
        if (failedUploads.length > 0) {
          setUploadError(`${failedUploads.length} photo(s) failed to upload`);
          setUploadState('error');
        } else {
          setUploadState('complete');
        }
      }

      return behaviorResponse;
    },
    onSuccess: async (response) => {
      if (mode === 'bulk') {
        // Type guard: bulk response has 'successful' and 'failed' properties
        if ('successful' in response && 'failed' in response) {
          const successCount = response.successful || 0;
          const failedCount = response.failed || 0;
          if (failedCount > 0) {
            const errors = (response as { errors: Array<{ studentId: string; reason: string }> }).errors ?? [];
            await alertDialog({
              title: `Failed for ${failedCount} student${failedCount > 1 ? 's' : ''}`,
              message: (
                <ul style={{ margin: 0, paddingLeft: '1.25rem', maxHeight: '60vh', overflowY: 'auto' }}>
                  {errors.map((err) => (
                    <li key={err.studentId}>
                      <strong>{studentNames?.[err.studentId] || 'Unknown student'}</strong>: {err.reason}
                    </li>
                  ))}
                </ul>
              ),
            });
            // If ALL students failed, keep the modal open so the teacher can adjust and retry
            if (successCount === 0) return;
          }
          // Show success alert with guidance on where to find bulk edit/delete
          if (successCount > 0) {
            const categoryName = category?.name ?? 'behavior';
            await alertDialog({
              title: 'Behavior Logged',
              message: (
                <>
                  <p style={{ margin: '0 0 1rem 0' }}>
                    Successfully logged <strong>{categoryName}</strong> for{' '}
                    <strong>{successCount} student{successCount > 1 ? 's' : ''}</strong>.
                  </p>
                  <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                    To edit or delete this bulk action, open any student's behavior history and look for the group action buttons.
                  </p>
                </>
              ),
            });
          }
        }
      } else {
        if (uploadState === 'error') {
          success('Behavior logged (some photos failed to upload)');
        } else if (selectedPhotos.length > 0) {
          success(`Behavior logged with ${selectedPhotos.length} photo(s)`);
        } else {
          success('Behavior logged successfully');
        }
      }
      try {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['myBlocksToday'] }),
          queryClient.invalidateQueries({ queryKey: ['behaviors'] }),
          queryClient.invalidateQueries({ queryKey: ['todayBehaviorCount'] }),
          queryClient.invalidateQueries({ queryKey: ['studentBehaviorHistory'] }),
          queryClient.invalidateQueries({ queryKey: ['studentBehaviorContexts'] }),
          queryClient.invalidateQueries({ queryKey: ['recent-behavior-audit'] }),
          queryClient.invalidateQueries({ queryKey: ['teacherStudentsByBlockAll'] }),
          queryClient.invalidateQueries({ queryKey: ['teacherStudentsByBlock'] }),
          queryClient.invalidateQueries({ queryKey: ['teacherStudentsMyGrades'] }),
          queryClient.invalidateQueries({ queryKey: ['teacherStudentsMy'] }),
          queryClient.invalidateQueries({ queryKey: ['teacherStudentsAllGrades'] }),
          queryClient.invalidateQueries({ queryKey: ['teacherStudentsAll'] }),
        ]);
      } catch {
        // Refetch failed — queries will refresh on next focus/interaction
      }
      onClose();
      resetForm();
    },
    onError: async (err) => {
      const errorCode = (err as { code?: string }).code;
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';

      // Warn mode: confirmation dialog, retry if user confirms
      if (errorCode === 'BEHAVIOR_BLOCK_LIMIT_WARNING') {
        setUploadState('idle');
        setUploadError(null);
        const confirmed = await confirmDialog({
          title: 'Behavior Already Logged',
          message: (
            <>
              {errorMessage}
              <br /><br />
              Log another anyway?
            </>
          ),
          confirmText: 'Log Anyway',
          cancelText: 'Cancel',
        });
        if (confirmed) {
          createMutation.mutate({ acknowledgeBlockLimit: true });
        }
        return;
      }

      // Block mode: informational alert (no override)
      if (errorCode === 'BEHAVIOR_BLOCK_LIMIT') {
        setUploadState('idle');
        setUploadError(null);
        await alertDialog({
          title: 'Behavior Limit Reached',
          message: errorMessage,
        });
        return;
      }

      // All other errors: reset upload state, show toast
      setUploadState(selectedPhotos.length > 0 ? 'error' : 'idle');
      if (selectedPhotos.length > 0) {
        setUploadError(errorMessage);
      }
      error(`Failed to log behavior: ${errorMessage}`);
    },
  });

  const handleSubmit = () => {
    // Validation
    if (!selectedCategory) {
      error('Please select a behavior category');
      return;
    }

    // In dropdown mode, require block selection when classroom is selected
    if (availableBlocks && availableBlocks.length > 0 && behaviorLocation === 'classroom' && !selectedBlockId) {
      error('Please select a block/period');
      return;
    }

    if (behaviorLocation === 'location' && !selectedLocation) {
      error('Please select a location');
      return;
    }

    if (notesRequired && !notes.trim()) {
      error('Notes are required for this behavior');
      return;
    }

    createMutation.mutate({});
  };

  // Defensive check: when availableBlocks is explicitly empty [], don't show toggle mode
  // This prevents showing "In class" option for blocks the teacher doesn't have permission to log
  const hasNoAvailableBlocks = Array.isArray(availableBlocks) && availableBlocks.length === 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'bulk' ? `Log Behavior for ${studentIds.length} Students` : `Log Behavior for ${studentName}`}
      maxWidth="620px"
    >
      <div style={modalContentStyle}>
        {/* Block Selection - Three modes based on props:
            1. availableBlocks provided → Dropdown mode
            2. blockId provided (no availableBlocks) → Toggle mode
            3. Neither provided → Location-only mode (support staff)
        */}
        {availableBlocks && availableBlocks.length > 0 ? (
          // Dropdown mode: Select from available blocks + "Outside class" option
          <div style={sectionStyle}>
            <label className="form-label">Where did this occur?</label>
            <select
              value={behaviorLocation === 'location' ? 'location' : selectedBlockId}
              onChange={(e) => {
                if (e.target.value === 'location') {
                  setBehaviorLocation('location');
                  setSelectedBlockId('');
                } else {
                  setBehaviorLocation('classroom');
                  setSelectedBlockId(e.target.value);
                }
              }}
              className="form-input"
            >
              <option value="">Select block/period...</option>
              {availableBlocks.map((block) => (
                <option key={block.id} value={block.id}>
                  {block.name}
                </option>
              ))}
              <option value="location">Outside class (select location)</option>
            </select>
          </div>
        ) : blockId && !hasNoAvailableBlocks ? (
          // Toggle mode: "In class" vs "Outside class" - only when teacher has permission
          <div style={sectionStyle}>
            <label className="form-label">Where did this occur?</label>
            <div style={toggleContainerStyle}>
              <button
                type="button"
                onClick={() => setBehaviorLocation('classroom')}
                style={{
                  ...toggleButtonStyle,
                  backgroundColor: behaviorLocation === 'classroom' ? 'var(--primary-color)' : 'transparent',
                  color: behaviorLocation === 'classroom' ? 'white' : 'var(--text-secondary)',
                  boxShadow: behaviorLocation === 'classroom' ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
                }}
              >
                In class ({blockName})
              </button>
              <button
                type="button"
                onClick={() => setBehaviorLocation('location')}
                style={{
                  ...toggleButtonStyle,
                  backgroundColor: behaviorLocation === 'location' ? 'var(--primary-color)' : 'transparent',
                  color: behaviorLocation === 'location' ? 'white' : 'var(--text-secondary)',
                  boxShadow: behaviorLocation === 'location' ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
                }}
              >
                Outside class
              </button>
            </div>
          </div>
        ) : null}

        {/* Location Button Grid (if outside class) */}
        {behaviorLocation === 'location' && (
          <div style={sectionStyle}>
            <label className="form-label">
              Location <span style={{ color: 'var(--error-color)' }}>*</span>
            </label>
            <div style={{ ...categoryGridStyle, gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(6, 1fr)' }}>
              {locations.map((loc) => (
                <button
                  key={loc.id}
                  type="button"
                  aria-pressed={selectedLocation === loc.id}
                  onClick={() => setSelectedLocation(loc.id)}
                  style={{
                    ...locationButtonStyle,
                    backgroundColor: selectedLocation === loc.id
                      ? `${loc.color}15`
                      : 'var(--surface-color)',
                    color: 'var(--text-primary)',
                    boxShadow: selectedLocation === loc.id
                      ? `0 0 0 2px ${loc.color}, 0 4px 12px rgba(0,0,0,0.1)`
                      : '0 1px 3px rgba(0,0,0,0.08)',
                    transform: selectedLocation === loc.id ? 'translateY(-2px)' : 'none',
                  }}
                >
                  <div style={{ color: loc.color }}>{getIcon(loc.icon, 20)}</div>
                  <div style={{ fontSize: '0.65rem', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%', textAlign: 'center' }}>{loc.name}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Category Selection */}
        <div style={sectionStyle}>
          <label className="form-label">
            Category <span style={{ color: 'var(--error-color)' }}>*</span>
          </label>
          <div style={{ ...categoryGridStyle, gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(6, 1fr)' }}>
            {categories.map((cat) => {
              const underlineColor = cat.isPositive
                ? 'var(--success-color)'
                : 'var(--error-color)';
              const underline = positiveBehaviorsEnabled
                ? `inset 0 -3px 0 ${underlineColor}`
                : '';
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setSelectedCategory(cat.id)}
                  style={{
                    ...categoryButtonStyle,
                    backgroundColor: selectedCategory === cat.id
                      ? `${cat.color}15`
                      : 'var(--surface-color)',
                    color: 'var(--text-primary)',
                    boxShadow: selectedCategory === cat.id
                      ? `0 0 0 2px ${cat.color}, 0 4px 12px rgba(0,0,0,0.1)${underline ? `, ${underline}` : ''}`
                      : underline
                        ? `0 1px 3px rgba(0,0,0,0.08), ${underline}`
                        : '0 1px 3px rgba(0,0,0,0.08)',
                    transform: selectedCategory === cat.id ? 'translateY(-2px)' : 'none',
                    position: positiveBehaviorsEnabled ? 'relative' : undefined,
                  }}
                >
                  {positiveBehaviorsEnabled && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '4px',
                        right: '4px',
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        backgroundColor: underlineColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      title={cat.isPositive ? 'Positive Behavior' : 'Negative Behavior'}
                    >
                      {cat.isPositive ? <Plus size={7} strokeWidth={3} color="white" /> : <Minus size={7} strokeWidth={3} color="white" />}
                    </div>
                  )}
                  <div style={{ color: cat.color }}>{getIcon(cat.icon || null)}</div>
                  <div style={{ fontSize: '0.75rem', fontWeight: '600' }}>{cat.name}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Behavior Date */}
        {selectedCategory && (
          <div style={sectionStyle}>
            <label className="form-label">Date</label>
            <input
              type="date"
              value={behaviorDate}
              onChange={(e) => setBehaviorDate(e.target.value)}
              max={getTodayInSchoolTZ(timezone)}
              min={(() => {
                const maxDays = schoolConfig?.behaviorBackdateDays ?? 7;
                const today = new Date(getTodayInSchoolTZ(timezone));
                today.setDate(today.getDate() - maxDays);
                return today.toISOString().split('T')[0];
              })()}
              className="form-input"
              style={{ width: '100%' }}
            />
          </div>
        )}

        {/* Severity Toggle - only when severity is enabled */}
        {severityEnabled && selectedCategory && (
          <div style={sectionStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <label className="form-label">
                Severity
                {isSeverityLocked && (
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', marginLeft: '0.5rem', textTransform: 'none', fontWeight: '400' }}>
                    (locked to default)
                  </span>
                )}
              </label>
              <button
                type="button"
                onClick={() => setSeverityInfoExpanded(!severityInfoExpanded)}
                style={infoIconButtonStyle}
                title="Learn more about severity"
              >
                <Info size={16} aria-hidden="true" />
              </button>
            </div>
            <div style={severityContainerStyle}>
              <button
                type="button"
                onClick={() => !isSeverityLocked && setSelectedSeverity('minor')}
                disabled={isSeverityLocked}
                style={{
                  ...severityButtonStyle,
                  backgroundColor: selectedSeverity === 'minor' ? 'var(--primary-color)' : 'transparent',
                  color: selectedSeverity === 'minor' ? 'white' : 'var(--text-secondary)',
                  boxShadow: selectedSeverity === 'minor' ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
                  opacity: isSeverityLocked ? 0.6 : 1,
                  cursor: isSeverityLocked ? 'not-allowed' : 'pointer',
                }}
              >
                Minor
                {category?.defaultSeverity === 'minor' && (
                  <span
                    style={{
                      fontSize: '0.5625rem',
                      backgroundColor: selectedSeverity === 'minor' ? 'rgba(255,255,255,0.25)' : 'var(--primary-color)',
                      color: 'white',
                      padding: '0.125rem 0.375rem',
                      borderRadius: '9999px',
                      fontWeight: '500',
                      textTransform: 'uppercase',
                      letterSpacing: '0.02em',
                    }}
                  >
                    {isSeverityLocked ? (isMobile ? 'def' : 'default') : (isMobile ? 'rec' : 'recommended')}
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => !isSeverityLocked && setSelectedSeverity('major')}
                disabled={isSeverityLocked}
                style={{
                  ...severityButtonStyle,
                  backgroundColor: selectedSeverity === 'major' ? 'var(--primary-color)' : 'transparent',
                  color: selectedSeverity === 'major' ? 'white' : 'var(--text-secondary)',
                  boxShadow: selectedSeverity === 'major' ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
                  opacity: isSeverityLocked ? 0.6 : 1,
                  cursor: isSeverityLocked ? 'not-allowed' : 'pointer',
                }}
              >
                Major
                {category?.defaultSeverity === 'major' && (
                  <span
                    style={{
                      fontSize: '0.5625rem',
                      backgroundColor: selectedSeverity === 'major' ? 'rgba(255,255,255,0.25)' : 'var(--primary-color)',
                      color: 'white',
                      padding: '0.125rem 0.375rem',
                      borderRadius: '9999px',
                      fontWeight: '500',
                      textTransform: 'uppercase',
                      letterSpacing: '0.02em',
                    }}
                  >
                    {isSeverityLocked ? (isMobile ? 'def' : 'default') : (isMobile ? 'rec' : 'recommended')}
                  </span>
                )}
              </button>
            </div>
            {/* Explanatory note about severity (toggleable) */}
            {severityInfoExpanded && (
              <div
                style={{
                  fontSize: '0.875rem',
                  color: 'var(--text-secondary)',
                  fontStyle: 'italic',
                  marginTop: '0.5rem',
                  padding: '0.75rem',
                  backgroundColor: 'var(--surface-color)',
                  borderRadius: '4px',
                  borderLeft: '3px solid var(--primary-color)',
                }}
              >
                {canOverrideSeverity
                  ? "The recommended severity is based on your school's guidelines. You may adjust if the situation warrants it."
                  : 'Your school requires the default severity to ensure consistent behavior tracking across all staff.'}
              </div>
            )}
          </div>
        )}

        {/* Photo Evidence Upload (optional, up to 3 photos) */}
        {selectedCategory && mode === 'single' && schoolConfig?.enableBehaviorPhotos && (
          <div style={sectionStyle}>
            <label className="form-label">
              Photo Evidence
              <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}> (up to 3 photos)</span>
            </label>

            <FileUpload
              accept="image/jpeg,image/png,image/webp"
              maxFiles={3}
              maxFileSize={10 * 1024 * 1024}
              multiple
              files={selectedPhotos}
              onFilesChange={setSelectedPhotos}
              onError={error}
              label="Add Photos"
              hint="Up to 3 · JPEG, PNG, WebP · 10MB max"
              icon={<Camera size={20} />}
              capture="environment"
              disabled={uploadState === 'uploading'}
            />

            {/* Upload progress */}
            {uploadState === 'uploading' && (
              <div
                style={{
                  marginTop: '0.75rem',
                  padding: '0.75rem',
                  backgroundColor: 'var(--surface-color)',
                  borderRadius: 'var(--radius)',
                  border: '1px solid var(--border-color)',
                }}
              >
                <div
                  style={{
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)',
                    marginBottom: '0.5rem',
                  }}
                >
                  Uploading photos... {Math.round(uploadProgress)}%
                </div>
                <div
                  style={{
                    height: '8px',
                    backgroundColor: 'var(--border-color)',
                    borderRadius: '4px',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${uploadProgress}%`,
                      backgroundColor: 'var(--primary-color)',
                      transition: 'width 0.3s ease',
                    }}
                  />
                </div>
              </div>
            )}

            {/* Upload error */}
            {uploadError && (
              <div
                style={{
                  marginTop: '0.75rem',
                  padding: '0.75rem',
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  borderRadius: 'var(--radius)',
                  border: '1px solid var(--error-color)',
                  color: 'var(--error-color)',
                  fontSize: '0.875rem',
                }}
              >
                {uploadError}
              </div>
            )}
          </div>
        )}

        {/* Points Override - only when points are enabled and override is allowed */}
        {pointsEnabled && allowPointOverride && selectedCategory && category && (
          <div style={sectionStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <label className="form-label">
                {term.points.pluralCap}
              </label>
              <button
                type="button"
                onClick={() => setPointsInfoExpanded(!pointsInfoExpanded)}
                style={infoIconButtonStyle}
                title={`Learn more about ${term.points.plural}`}
              >
                <Info size={16} aria-hidden="true" />
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="number"
                  value={pointsOverride ?? category.defaultPoints}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    if (!isNaN(val)) {
                      // Clamp to bounds
                      const clamped = Math.max(pointOverrideMin, Math.min(pointOverrideMax, val));
                      setPointsOverride(clamped);
                    }
                  }}
                  min={pointOverrideMin}
                  max={pointOverrideMax}
                  style={{
                    ...pointsInputStyle,
                    borderColor: pointsOverride !== null ? 'var(--primary-color)' : 'var(--border-color)',
                  }}
                />
                <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  ({pointOverrideMin}-{pointOverrideMax})
                </span>
              </div>
              {/* Show indicator for positive/negative impact - neutral styling for 0 points */}
              <div
                style={{
                  padding: '0.375rem 0.75rem',
                  borderRadius: 'var(--radius)',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  backgroundColor: effectivePoints === 0
                    ? 'var(--neutral-bg)'
                    : category.isPositive
                      ? 'var(--success-bg)'
                      : 'var(--error-bg)',
                  color: effectivePoints === 0
                    ? 'var(--text-secondary)'
                    : category.isPositive
                      ? 'var(--success-color)'
                      : 'var(--error-color)',
                }}
              >
                {effectivePoints === 0 ? '' : (category.isPositive ? '+' : '-')}{effectivePoints} {term.points.abbreviation}
              </div>
              {/* Reset button when overridden */}
              {pointsOverride !== null && (
                <button
                  type="button"
                  onClick={() => setPointsOverride(null)}
                  style={{
                    fontSize: '0.75rem',
                    color: 'var(--text-secondary)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                >
                  Reset to default ({category.defaultPoints})
                </button>
              )}
            </div>
            {/* Explanatory note about points (toggleable) */}
            {pointsInfoExpanded && (
              <div
                style={{
                  fontSize: '0.875rem',
                  color: 'var(--text-secondary)',
                  fontStyle: 'italic',
                  marginTop: '0.5rem',
                  padding: '0.75rem',
                  backgroundColor: 'var(--surface-color)',
                  borderRadius: '4px',
                  borderLeft: `3px solid ${
                    effectivePoints === 0
                      ? 'var(--border-color)'
                      : category.isPositive
                        ? 'var(--success-color)'
                        : 'var(--primary-color)'
                  }`,
                }}
              >
                {category.isPositive
                  ? `${term.points.pluralCap} for positive behaviors are added to the student's net score.`
                  : `${term.points.pluralCap} for negative behaviors are subtracted from the student's net score.`}
                {' '}Default: {category.defaultPoints} {term.pointCount(category.defaultPoints)}.
              </div>
            )}
          </div>
        )}

        {/* Points Display (read-only) - when points enabled but override not allowed */}
        {pointsEnabled && !allowPointOverride && selectedCategory && category && (
          <div style={sectionStyle}>
            <label className="form-label">{term.points.pluralCap}</label>
            <div
              style={{
                padding: '0.5rem 0.75rem',
                borderRadius: 'var(--radius)',
                fontSize: '0.875rem',
                fontWeight: '500',
                backgroundColor: category.defaultPoints === 0
                  ? 'var(--neutral-bg)'
                  : category.isPositive
                    ? 'var(--success-bg)'
                    : 'var(--error-bg)',
                color: category.defaultPoints === 0
                  ? 'var(--text-secondary)'
                  : category.isPositive
                    ? 'var(--success-color)'
                    : 'var(--error-color)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.25rem',
              }}
            >
              {category.defaultPoints === 0 ? '' : (category.isPositive ? '+' : '-')}{category.defaultPoints} {term.points.abbreviation}
              <span style={{ fontSize: '0.75rem', opacity: 0.8 }}>(default)</span>
            </div>
          </div>
        )}

        {/* Notes Field */}
        {selectedCategory && (
          <div style={sectionStyle}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label className="form-label" style={{ margin: 0 }}>
                Notes
                {notesRequired && <span style={{ color: 'var(--error-color)' }}> *</span>}
                {!notesRequired && (
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-tertiary)' }}> (optional)</span>
                )}
              </label>
              {positiveBehaviorsEnabled && !pointsEnabled && category && (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  fontSize: '0.7rem',
                  fontWeight: '600',
                  padding: '0.125rem 0.5rem',
                  borderRadius: '9999px',
                  backgroundColor: category.isPositive ? 'var(--success-bg)' : 'var(--error-bg)',
                  color: category.isPositive ? 'var(--success-color)' : 'var(--error-color)',
                }}>
                  {category.isPositive ? <Plus size={10} strokeWidth={3} /> : <Minus size={10} strokeWidth={3} />}
                  {category.isPositive ? 'Positive' : 'Negative'}{!isMobile && ' Behavior'}
                </span>
              )}
            </div>
            <div>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={
                  notesRequired ? 'Please provide details about this behavior' : 'Add any additional notes (optional)'
                }
                rows={3}
                maxLength={maxNotesLength}
                style={textareaStyle}
              />
              <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', textAlign: 'right', marginTop: '0.25rem', opacity: 0.7 }}>
                {notes.length}/{maxNotesLength}
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div style={actionsStyle}>
          <button onClick={onClose} style={buttonStyles.cancel}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={createMutation.isPending || !selectedCategory}
            style={{
              ...submitButtonStyle,
              opacity: !selectedCategory ? 0.5 : 1,
              cursor: !selectedCategory ? 'not-allowed' : 'pointer',
            }}
          >
            {createMutation.isPending
              ? 'Logging...'
              : mode === 'bulk'
                ? `Log for ${studentIds.length} Students`
                : 'Log Behavior'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// Styles
const modalContentStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1.25rem',
};

const sectionStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
};

const categoryGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(6, 1fr)',
  gap: '0.5rem',
};

const categoryButtonStyle: React.CSSProperties = {
  padding: '1rem 0.5rem',
  borderRadius: '12px',
  border: 'none',
  cursor: 'pointer',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  minHeight: '80px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.5rem',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
};

const locationButtonStyle: React.CSSProperties = {
  padding: '0.625rem 0.25rem',
  borderRadius: '8px',
  border: 'none',
  cursor: 'pointer',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  minHeight: '62px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.25rem',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
};

const toggleContainerStyle: React.CSSProperties = {
  display: 'flex',
  backgroundColor: 'var(--surface-color)',
  borderRadius: '10px',
  padding: '4px',
  gap: '4px',
  border: '1px solid var(--border-color)',
};

const toggleButtonStyle: React.CSSProperties = {
  flex: 1,
  padding: '0.625rem 1rem',
  borderRadius: '8px',
  border: 'none',
  cursor: 'pointer',
  fontWeight: '500',
  fontSize: '0.875rem',
  transition: 'all 0.2s ease',
  minHeight: '40px',
};

const severityContainerStyle: React.CSSProperties = {
  display: 'flex',
  backgroundColor: 'var(--surface-color)',
  borderRadius: '10px',
  padding: '4px',
  gap: '4px',
  border: '1px solid var(--border-color)',
};

const severityButtonStyle: React.CSSProperties = {
  flex: 1,
  padding: '0.5rem 0.75rem',
  borderRadius: '8px',
  border: 'none',
  cursor: 'pointer',
  fontWeight: '500',
  fontSize: '0.875rem',
  transition: 'all 0.2s ease',
  minHeight: '36px',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.5rem',
};

const infoIconButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'var(--text-secondary)',
  cursor: 'pointer',
  padding: '0.25rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  transition: 'all 0.15s ease',
  opacity: 0.6,
};

const pointsInputStyle: React.CSSProperties = {
  width: '80px',
  padding: '0.5rem 0.75rem',
  borderRadius: '8px',
  border: '1px solid var(--border-color)',
  fontSize: '0.9375rem',
  fontWeight: '500',
  textAlign: 'center',
  backgroundColor: 'var(--background-color)',
  color: 'var(--text-primary)',
  transition: 'border-color 0.15s ease',
};

const textareaStyle: React.CSSProperties = {
  padding: '0.875rem',
  borderRadius: '10px',
  border: '1px solid var(--border-color)',
  fontSize: '0.9375rem',
  resize: 'vertical',
  minHeight: '72px',
  fontFamily: 'inherit',
  backgroundColor: 'var(--background-color)',
  color: 'var(--text-primary)',
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  width: '100%',
  boxSizing: 'border-box',
};

const actionsStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '1rem',
};

const submitButtonStyle: React.CSSProperties = {
  padding: '0.625rem 1.5rem',
  borderRadius: '8px',
  border: 'none',
  backgroundColor: 'var(--primary-color)',
  color: 'white',
  cursor: 'pointer',
  fontWeight: '600',
  fontSize: '0.875rem',
  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  transition: 'all 0.15s ease',
  minHeight: '40px',
};
